import type * as C from "@zooi/contracts";
import { createContext, useContext } from "react";
import { readPref, writePref } from "./prefs.ts";

// Which inbox items this browser has opened (docs/design-system.md §0.4). A
// per-device convenience: another device may still show an item as new, and
// when storage is unavailable every item reads as new, never as seen.

const MAX_SEEN = 200;
const prefKey = (scope: string) => `inbox-seen:${scope}`;

/** An item that leaves the inbox and comes back, such as a work order failing again, is new again. */
export const seenToken = (item: C.InboxItem) => `${item.key}@${item.created_at}`;

export function readSeen(scope: string): Set<string> {
  try {
    const list: unknown = JSON.parse(readPref(prefKey(scope)) ?? "[]");
    return new Set(Array.isArray(list) ? list.filter((value): value is string => typeof value === "string") : []);
  } catch {
    return new Set();
  }
}

export function writeSeen(scope: string, seen: Set<string>) {
  writePref(prefKey(scope), JSON.stringify([...seen].slice(-MAX_SEEN)));
}

/** Keeps only items still in the inbox; returns the same set when nothing left. */
export function pruneSeen(seen: Set<string>, items: C.InboxItem[]): Set<string> {
  const current = new Set(items.map(seenToken));
  const kept = [...seen].filter((token) => current.has(token));
  return kept.length === seen.size ? seen : new Set(kept);
}

/** Items that were not in the previous read of the inbox. */
export const arrivals = (previous: Set<string>, items: C.InboxItem[]) => items.filter((item) => !previous.has(seenToken(item)));

export interface InboxSeen {
  isNew: (item: C.InboxItem) => boolean;
  markSeen: (item: C.InboxItem) => void;
}
export const InboxSeenContext = createContext<InboxSeen>({ isNew: () => false, markSeen: () => {} });
export const useInboxSeen = () => useContext(InboxSeenContext);
